// ============================================================================
// src/seanceServeur.js : les gestes de séance passent par le serveur (lot 4)
// ============================================================================
// Fermeture des règles, lot 4 (18/10/2026). Plus aucune écriture du navigateur
// sur la séance en direct (sessions/current) ni sur l'atelier d'amendements :
//   • l'équipe (console) : fonction seanceAnimer (administrateur, jeton Aramis) ;
//   • le votant, AVEC OU SANS compte : fonction seanceVoter. Il s'identifie par
//     son code, son lien nominatif ou sa session Aramis, émarge, et reçoit un
//     JETON DE VOTANT (gardé dans ce navigateur pour la séance) qui porte
//     chacun de ses gestes ;
//   • la lecture SANS compte : seancePublique/current (useSeancePublique), la
//     question, ses choix et des résultats anonymes, rien de plus.
// Les deux fonctions sont à Francfort (europe-west3).
// ============================================================================
import { useEffect, useState } from "react";
import { REGION_FRANCFORT } from "./regionsFonctions";

async function appeler(nom, data, timeout = 30000) {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  try {
    const res = await httpsCallable(getFunctions(undefined, REGION_FRANCFORT), nom, { timeout })(data);
    return res.data;
  } catch (e) {
    // Défi du mode strict (et de seanceAnimer, qui exige le jeton Aramis) :
    // la bannière de reconnexion s'affiche, l'erreur suit son cours.
    try { const g = await import("./authGuard"); g.signalerSiDefi(e); } catch {}
    throw e;
  }
}

/** L'équipe : geste de console (ping, ecrire, amendement*). */
export const animer = (action, data = {}) => appeler("seanceAnimer", { ...data, action });
/** Le votant : geste par jeton (ou identification, émargement). */
export const voterSeance = (action, data = {}) => appeler("seanceVoter", { ...data, action });

/** Message lisible d'un refus du serveur (sans le préfixe de code anglais). */
export const messageServeur = (e, repli) => {
  const m = String((e && e.message) || "").replace(/^[a-z-]+:\s*/i, "").trim();
  return m && !/^internal$/i.test(m) ? m : repli;
};

// ── Lien d'accès des votants (QR code de la console et de l'écran de salle) ─
// Recette du 18/10/2026 : le QR code ouvrait l'accueil (« Ma semaine » pour
// un membre connecté, la page de garde sinon), jamais le vote. Il ouvre
// désormais l'espace du votant de CETTE séance, avec ou sans compte : App.jsx
// lit `view=participant` et `sid` comme un lien de vote (magicVote), sans
// participant désigné (pid), donc sans rien préremplir : le votant
// s'identifie par son code ou par sa session Aramis.
export const urlAccesSeance = (sessionId, origine = (typeof window !== "undefined" ? window.location.origin : "")) =>
  sessionId ? `${origine}/?view=participant&sid=${encodeURIComponent(sessionId)}` : `${origine}/?view=participant`;
/** Le même lien, lisible à l'écran (sans le protocole). */
export const urlAccesLisible = (sessionId, origine) => urlAccesSeance(sessionId, origine).replace(/^https?:\/\//, "");

// ── Mémoire locale du votant : { sessionId, participantId, jeton } ──────────
const CLE = "eniovote_participant";
export function lireVotantLocal(sessionId) {
  try {
    const v = JSON.parse(localStorage.getItem(CLE) || "null");
    return v && v.sessionId === sessionId && v.participantId && v.jeton ? v : null;
  } catch { return null; }
}
export function ecrireVotantLocal(v) {
  try { localStorage.setItem(CLE, JSON.stringify(v)); } catch {}
}
export function oublierVotantLocal() {
  try { localStorage.removeItem(CLE); } catch {}
}

// ── Lecture publique de la séance ────────────────────────────────────────────
export const PROJECTION_VIDE = { session: null, agenda: [], votesByScrutin: {}, organisations: [], nbActifs: 0, liveDisplay: null };

/** seancePublique/current, en direct. `actif` faux : aucune écoute. */
export function useSeancePublique(actif = true) {
  const [etat, setEtat] = useState({ projection: PROJECTION_VIDE, chargee: false });
  useEffect(() => {
    if (!actif) return undefined;
    let fin = null;
    let annule = false;
    (async () => {
      const [{ doc, onSnapshot }, { db }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
      if (annule) return;
      fin = onSnapshot(doc(db, "seancePublique", "current"),
        (snap) => setEtat({ projection: snap.exists() ? { ...PROJECTION_VIDE, ...snap.data() } : PROJECTION_VIDE, chargee: true }),
        (err) => { console.warn("[séance] lecture publique :", err && err.code); setEtat((e) => ({ ...e, chargee: true })); });
    })();
    return () => { annule = true; if (fin) fin(); };
  }, [actif]);
  return etat;
}

/** La lecture publique, à la forme d'appState que lisent l'écran de salle
 *  et l'espace du votant (aucun participant : le décompte vient de nbActifs). */
export const versEtatSalle = (p) => ({
  session: p.session || null, participants: [], agenda: p.agenda || [], votesByScrutin: p.votesByScrutin || {},
  liveDisplay: p.liveDisplay || null, nbActifs: p.nbActifs || 0, organisations: p.organisations || [], history: [],
});
